import React, { useState, useEffect, useContext, useRef } from "react";
import Map from "../components/Map";
import {
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  FormControlLabel,
  Checkbox,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
} from "@mui/material";
import { saveAs } from "file-saver";
import CircularProgress from "@mui/material/CircularProgress"; // Import CircularProgress for loading indicator

import makeStyles from "@mui/styles/makeStyles";
import { locationObj, cropLabels } from "../util/mapUtils";
import { store } from "../contexts/store";
import {
  setIndex,
  setFBILevel,
  setYear,
  setCropYear,
  setSeason,
} from "../contexts/actions";
import { CloseIcon, DocumentationIcon } from "../icons";

import FieldStats from "../components/FieldStats";
import TimeSeriesChart from "../components/TimseSeriesChart";
import useMediaQuery from "@mui/material/useMediaQuery";
import {
  fields,
  hectares,
  countryCode,
  region,
  localMin,
  localMax,
  configObj,
  indexNames,
} from "../config";
import styles from "../components/fieldsStats.module.css";
import Legend from "../components/Legend";
import FileUpload from "../components/FileUpload";
import { Logout, LogoutOutlined } from "@mui/icons-material";

const LOCALSTORAGE_KEYS = {
  accessToken: "map_access_token",
};

const API_URL =
  "https://01pelhey2k.execute-api.eu-central-1.amazonaws.com/development/sectional-control/token";

const useStyles = makeStyles((theme) => ({
  yearSelectionModal: {
    "& .MuiDialog-paper": {
      padding: theme.spacing(3),
      maxWidth: 400,
      width: "90%",
    },
  },
  yearGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(100px, 1fr))",
    gap: theme.spacing(2),
    padding: theme.spacing(2),
  },
  yearButton: {
    padding: theme.spacing(2),
    borderRadius: theme.spacing(1),
    border: "1px solid #d4d8dc",
    backgroundColor: "#fff",
    "&:hover": {
      backgroundColor: "#f5f5f5",
      borderColor: "#01b075",
    },
  },
  yearButtonSelected: {
    backgroundColor: "#01b075",
    color: "#fff",
    "&:hover": {
      backgroundColor: "#01b075",
    },
  },
  exportButton: {
    display: "flex",
    borderRadius: 4,
    textTransform: "capitalize",
    fontWeight: 600,
    fontSize: 16,
    color: "#01b075",
    borderColor: "#d4d8dc",
    backgroundColor: "#fff",
    padding: "8px 16px",
    outline: "none",
    boxShadow: "none",
    border: "1px solid #d4d8dc",
    width: "97%",
    alignItems: "center",
    justifyContent: "center",
    "&:hover": {
      borderColor: "#01b075",
      backgroundColor: "#fff",
    },
  },
  exportButtonContainer: {
    backgroundColor: "#f8f9fa",
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
    padding: "8px 16px",
    position: "absolute", // Ensure it stays at the bottom
    bottom: 0, // Align to the bottom of the dialog
    width: "100%", // Full width of the dialog
    textAlign: "center", // Center the button
  },
  circularProgressContainer: {
    position: "fixed",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    display: "flex",
    flexDirection: "column", // Stack items vertically
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "rgba(255, 255, 255, 0.9)",
    padding: 30,
    zIndex: 10,
    boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
    borderRadius: 12,
    animation: "$fadeIn 0.5s ease-in-out", // Add fade-in animation
  },
  circularProgress: {
    color: theme.palette.primary.main,
    width: 50,
    height: 50,
    marginBottom: 15, // Add space between loader and text
  },
  loadingText: {
    color: theme.palette.text.primary,
    fontWeight: 600,
    fontSize: "1.2rem",
    fontFamily: "'Roboto', sans-serif", // Use a modern font
  },
  "@keyframes fadeIn": {
    "0%": { opacity: 0 },
    "100%": { opacity: 1 },
  },

  fieldResponseContainer: {
    position: "absolute",
    bottom: 80,
    width: "97%",
    left: "1.5%",
    backgroundColor: "#fff",
    padding: "20px",
    zIndex: 1,
    boxShadow: "0px -7px 32px 0px rgba(170,170,170,1)", // Softer shadow for a modern look
    borderRadius: 24, // Slightly smaller radius for a cleaner look
    transition: "all 0.3s ease-in-out", // Smooth transition for any changes
    "@media (max-width: 600px)": {
      width: "95%",
      left: "2.5%",
      padding: "15px",
      bottom: 40,
    },
    "@media (min-width: 601px) and (max-width: 960px)": {
      width: "90%",
      left: "5%",
      padding: "18px",
      bottom: 60,
    },
    "&:hover": {
      boxShadow: "0px 6px 24px rgba(0, 0, 0, 0.15)", // Slightly larger shadow on hover
    },
    background: "linear-gradient(135deg, #f8f9fa, #e9ecef)", // Subtle gradient for depth
  },

  dropdownContainer: {
    position: "absolute",
    top: 64,
    left: 16,
    zIndex: 4,
    "& >div": {
      marginRight: 8,
      "@media (max-width: 600px)": {
        marginBottom: 8,
      },
    },
  },
  select: {
    paddingTop: 16,
    paddingBottom: 12,
    backgroundColor: "#fff",
    minWidth: 80,
    textAlign: "left",
    fontWeight: 600,
    fontSize: 14,
    "&:focus": {
      backgroundColor: "#fff",
      borderRadius: 8,
      border: "none",
    },
  },
  selectLabel: {
    marginTop: 12,
    fontSize: 12,
  },
  formControl: {
    "&:hover": {
      border: "1px solid #949EA7",
    },
    // Mui focused seems to be creating a green border.  fix that
  },
  outlined: {},
  selectIcon: {
    color: theme.palette.primary.main,
  },
  toggleButtonGroup: {
    backgroundColor: "#fff",
    border: "none",
    display: "inline-block",
    borderRadius: 8,
    padding: 4,
    marginLeft: 16,
  },
  toggleButton: {
    fontWeight: 600,
    textTransform: "capitalize",
    backgroundColor: "#fff",
    boxShadow: "none",
    borderRadius: 4,
    "&:hover": {
      backgroundColor: theme.palette.primary.light,
      boxShadow: "none",
    },
  },
  toggleButtonActive: {
    backgroundColor: theme.palette.primary.main,
    "&:hover": {
      backgroundColor: theme.palette.primary.dark,
    },
  },
  aboutApiQueries: {
    position: "absolute",
    bottom: 16,
    left: 16,
    zIndex: 4,
    lineHeight: "16px",
  },
  aboutApiQueriesButton: {
    backgroundColor: "#fff",
    "&:hover": {
      backgroundColor: "#fff",
    },
    textTransform: "capitalize",
    color: "#01B075",
    fontWeight: 600,
  },
  infoIcon: {
    fill: "none",
    stroke: "#01B075",
    marginRight: 4,
  },
  iconRoot: {
    fill: "none",
  },
  infoCardContainer: {
    position: "absolute",
    bottom: 24,
    left: 8,
    backgroundColor: "#fff",
    width: 480,
    maxWidth: "100vw",
    maxHeight: 540,
    borderRadius: 8,
    "@media (max-width: 600px)": {
      // zIndex: 2,
      left: 0,
      // top: "64vh",
      width: "100vw",
      bottom: 80,
    },
  },
  drSelectorContainer: {
    position: "absolute",
    bottom: 24,
    right: 16,
    maxWidth: 478,
    "@media (max-width: 600px)": {
      left: 0,
      // top: "68vh",
      // width: "100vw",
      maxWidth: "unset",
      width: "100%",
      overflowX: "scroll",
      height: 46,
    },
  },
  showCrops: {
    backgroundColor: "#fff",
    padding: "4px 0px 4px 8px",
    display: "inline-flex",
    borderRadius: 8,

    border: "1px solid rgba(0, 0, 0, 0.23)",
  },
  showCropsLabel: {
    fontWeight: "700",
    fontSize: "14px",
  },
  topRightContainer: {
    position: "absolute",
    top: 10,
    right: 62,
    zIndex: 1,
  },
  fileUploadMobile: {
    display: "inline-flex",
  },
  nav: {
    position: "absolute",
    top: 0,
    width: "100%",
    backgroundColor: "#1D2023",
    // minHeight: 48,
    padding: "12px 16px",
    zIndex: 1,
    display: "flex",
    alignItems: "center",
    color: "#fff",
  },
  logo: {
    maxHeight: 32,
    "@media (max-width: 600px)": {
      maxHeight: 24,
    },
  },
  authModal: {
    "& .MuiDialog-paper": {
      padding: theme.spacing(2),
      minWidth: 300,
    },
  },
  logoutButton: {
    position: "absolute",
    // top: 10,
    right: 160,
    color: "#fff",
    "@media (max-width: 600px)": {
      right: 100,
    },
  },
}));

const MapPage = () => {
  const classes = useStyles();
  const mobileView = useMediaQuery("(max-width:600px)");

  const {
    state: { feature, fbiLevel, indexName, year, cropYear, season },
    dispatch,
  } = useContext(store);

  const [level, setLevel] = useState("local");
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [openAuthModal, setOpenAuthModal] = useState(false);
  const [authToken, setAuthToken] = useState("");
  const [authError, setAuthError] = useState(false);
  const [fieldResponseJson, setFieldResponseJson] = useState();
  const [responseJson, setResponseJson] = useState();
  const [responseStatus, setResponseStatus] = useState();
  const [startDate, setStartDate] = React.useState(new Date("2017-01-01"));
  const [endDate, setEndDate] = React.useState(new Date(Date.now()));
  const [fieldresponseStatus, setFieldResponseStatus] = useState();
  const [isTimeSeriesModalOpen, setIsTimeSeriesModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedFieldData, setSelectedFieldData] = useState(null); // New state for field data
  const [timeseriesYear, setTimeseriesYear] = useState(2018);
  const [isYearSelectionModalOpen, setIsYearSelectionModalOpen] =
    useState(false);
  const [tempFieldData, setTempFieldData] = useState(null);

  const downloadGeoJSON = () => {
    const blob = new Blob([JSON.stringify(fieldResponseJson, null, 2)], {
      type: "application/json",
    });
    saveAs(blob, `FieldResponse-${selectedFieldData}.json`);
  };

  // useEffect(() => {
  //   console.log("fieldResponseJson:", fieldResponseJson);
  // }, [fieldResponseJson]);

  useEffect(() => {
    // Check authentication on component mount
    const storedToken = window.localStorage.getItem(
      LOCALSTORAGE_KEYS.accessToken
    );
    const enteredToken = window.localStorage.getItem("entered_api_token");

    if (storedToken && enteredToken && storedToken === enteredToken) {
      setIsAuthenticated(true);
    } else {
      setOpenAuthModal(true);
    }
  }, []);

  const fetchByFieldId = (fieldData, currentYear) => {
    // console.log(fieldData, currentYear);
    setIsLoading(true);

    const fetchUrl =
      `https://t2o8hpilj5.execute-api.eu-central-1.amazonaws.com/eni-dev/indices-timeseries/${fieldData.id}_37MCT_ke_1207357103065781_202407` +
      "?token=" +
      "a0731a8c-5259-4c68-af3a-7ad4f6d53faa" +
      "&names=" +
      "EVI,EVI2,NDVI,NDMI,MI,B01,B02,B03,B04,B05,B06,B07,B08,B08A,B09,B11,B12" +
      "&year=" +
      currentYear;

    const maxRetries = 10; // Maximum number of retry attempts
    let attempts = 0;

    const fetchData = () => {
      fetch(fetchUrl)
        .then((response) => {
          if (!response.ok) {
            throw new Error("Network response was not ok");
          }
          setFieldResponseStatus(response.status);
          return response.json();
        })
        .then((data) => {
          setFieldResponseJson(data);
          setSelectedFieldData(fieldData && fieldData.id);
          setIsLoading(false);
          setIsTimeSeriesModalOpen(true);
        })
        .catch((error) => {
          attempts += 1;
          if (attempts < maxRetries) {
            // console.log(`Retrying... (${attempts}/${maxRetries})`);
            fetchData(); // Retry fetching data
          } else {
            setIsLoading(false);
            setResponseStatus("Error");
            setResponseJson(null);
            console.error(
              "Failed to fetch data after multiple attempts:",
              error
            );
          }
        });
    };

    fetchData(); // Initial fetch call
  };

  const handleFieldClick = (fieldData) => {
    if (indexName === "timeseries" && fieldData) {
      setTempFieldData(fieldData);
      setIsYearSelectionModalOpen(true);
    }
  };
  const handleYearSelection = (selectedYear) => {
    setTimeseriesYear(selectedYear);
    setIsYearSelectionModalOpen(false);
    if (tempFieldData) {
      fetchByFieldId(tempFieldData, selectedYear);
    }
  };

  const handleCloseTimeSeriesModal = () => {
    setIsTimeSeriesModalOpen(false);
  };

  const handleLogin = async (e) => {
    // In this function make a call with the auth token to https://vectors.digifarm.io/v1/vectors/test/test/5/9/15.mvt?table=_mgrs&token={token}&limit=1000 and if it returns a 200 status code, then set the auth token in localStorage and set the isAuthenticated state to true
    try {
    const response = await fetch(
      `https://vectors.digifarm.io/development/vectors/test/test/12/2479/2073.mvt?table=demo_kenya_cardano_37mct_ts_new&token=${authToken}`
    );
    // Check for response status and size of response
    if (response.status === 200 && response.headers.get("Content-Length") > 0) {
      console.log("authToken", authToken);
      window.localStorage.setItem(LOCALSTORAGE_KEYS.accessToken, authToken);
      window.localStorage.setItem("entered_api_token", authToken);
      setIsAuthenticated(true);
      setOpenAuthModal(false);
      setAuthError(false);
    } else {
      setAuthError(true);
    }
  } catch (error) {
    setAuthError(true);
  }


    e.preventDefault();

    // try {
    //   const response = await fetch(`${API_URL}?token=${authToken}`);

    //   if (response.status === 200) {
    //     // Store tokens in localStorage
    //     window.localStorage.setItem(LOCALSTORAGE_KEYS.accessToken, authToken);
    //     window.localStorage.setItem("entered_api_token", authToken);

    //     setIsAuthenticated(true);
    //     setOpenAuthModal(false);
    //     setAuthError(false);
    //   } else {
    //     setAuthError(true);
    //   }
    // } catch (error) {
    //   setAuthError(true);
    // }
  };

  const handleLogout = () => {
    // Clear all localStorage items
    for (const property in LOCALSTORAGE_KEYS) {
      window.localStorage.removeItem(LOCALSTORAGE_KEYS[property]);
    }
    window.localStorage.removeItem("entered_api_token");

    // Reset authentication state
    setIsAuthenticated(false);
    setOpenAuthModal(true);
  };

  // Authentication Modal
  const AuthModal = () => (
    <Dialog
      open={openAuthModal}
      onClose={() => {}}
      className={classes.authModal}
      disableEscapeKeyDown
      disableBackdropClick
    >
      <DialogTitle>Authentication Required</DialogTitle>
      <DialogContent>
        <form onSubmit={handleLogin}>
          <TextField
            autoFocus
            margin="dense"
            label="Authentication Token"
            type="text"
            fullWidth
            value={authToken}
            onChange={(e) => {
              setAuthToken(e.target.value);
              setAuthError(false);
            }}
            error={authError}
            helperText={authError ? "Invalid Token. Please try again." : ""}
          />
        </form>
        <Typography
          variant="body2"
          color="textSecondary"
          style={{ marginTop: 16 }}
        >
          If you are unable to login, please{" "}
          <a href="mailto:hello@digifarm.io">contact us</a>.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleLogin} color="primary" variant="contained">
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );

  if (!isAuthenticated) {
    return <AuthModal />;
  }

  return (
    <div>
      <nav className={classes.nav}>
        <div>
          <div>
            <img
              src="/digifarm_logo.png"
              className={classes.logo}
              alt="Digifarm logo"
            />
            <img
              src="/Cardano-RGB_Logo-Full-Blue.png"
              className={classes.logo}
              alt="Cardano logo"
            />
          </div>
        </div>
        <div>
          {countryCode ? (
            <div className={styles.navCountryImg}>
              <img
                src={`https://flagcdn.com/32x24/${countryCode}.png`}
                width="24"
                height="auto"
                className="ml-2"
                alt={countryCode}
              ></img>
            </div>
          ) : (
            ""
          )}
        </div>

        {region ? (
          <div className={styles.navInfo}>
            <div>{region}</div>
            <Typography variant="caption" color={"#949EA7"}>
              Region
            </Typography>
          </div>
        ) : (
          ""
        )}
        {fields ? (
          <div className={styles.navInfo}>
            <div>{configObj[year].fields}</div>
            <Typography variant="caption" color={"#949EA7"}>
              Fields
            </Typography>
          </div>
        ) : (
          ""
        )}
        {hectares ? (
          <div className={styles.navInfo}>
            <div>{configObj[year].hectares}</div>
            <Typography variant="caption" color={"#949EA7"}>
              Hectares
            </Typography>
          </div>
        ) : (
          ""
        )}
        <IconButton
          onClick={handleLogout}
          variant="outlined"
          color="inherit"
          size="small"
          className={classes.logoutButton}
        >
          <Logout />
        </IconButton>
      </nav>
      <div className={classes.dropdownContainer}>
        <FormControl>
          <InputLabel
            shrink
            id="demo-simple-select-placeholder-label-label"
            className={classes.selectLabel}
          >
            Layer
          </InputLabel>
          <Select
            variant="outlined"
            labelId="demo-simple-select-placeholder-label-label"
            id="demo-simple-select-placeholder-label"
            classes={{
              select: classes.select,
              icon: classes.selectIcon,
              outlined: classes.outlined,
            }}
            value={indexName}
            defaultValue={"world"}
            onChange={(event) => {
              dispatch({
                type: setIndex,
                payload: { indexName: event.target.value },
              });
            }}
            MenuProps={{
              anchorOrigin: {
                vertical: "bottom",
                horizontal: "left",
              },
              transformOrigin: {
                vertical: "top",
                horizontal: "left",
              },
              getContentAnchorEl: null,
            }}
          >
            {configObj.layers.map((layer) => (
              <MenuItem value={layer}>{indexNames[layer]}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* 
        {indexName !== "Deforestation" && indexName !== "crop" && (
          <FormControl>
            <InputLabel
              shrink
              id="demo-simple-select-placeholder-label-label"
              className={classes.selectLabel}
            >
              Year
            </InputLabel>
            <Select
              variant="outlined"
              labelId="demo-simple-select-placeholder-label-label"
              id="demo-simple-select-placeholder-label"
              classes={{
                select: classes.select,
                icon: classes.selectIcon,
                outlined: classes.outlined,
              }}
              value={year}
              defaultValue={"world"}
              onChange={(event) => {
                dispatch({
                  type: setYear,
                  payload: { year: event.target.value },
                });
              }}
              MenuProps={{
                anchorOrigin: {
                  vertical: "bottom",
                  horizontal: "left",
                },
                transformOrigin: {
                  vertical: "top",
                  horizontal: "left",
                },
                getContentAnchorEl: null,
              }}
            >
              {configObj["years"].map((year) => (
                <MenuItem value={year}>{year}</MenuItem>
              ))}

         
            </Select>
          </FormControl>
        )}

        */}

        {indexName === "crop" && (
          <FormControl>
            <InputLabel
              shrink
              id="demo-simple-select-placeholder-label-label"
              className={classes.selectLabel}
            >
              Year
            </InputLabel>
            <Select
              variant="outlined"
              labelId="demo-simple-select-placeholder-label-label"
              id="demo-simple-select-placeholder-label"
              classes={{
                select: classes.select,
                icon: classes.selectIcon,
                outlined: classes.outlined,
              }}
              value={cropYear}
              defaultValue={"world"}
              onChange={(event) => {
                dispatch({
                  type: setCropYear,
                  payload: { cropYear: event.target.value },
                });
              }}
              MenuProps={{
                anchorOrigin: {
                  vertical: "bottom",
                  horizontal: "left",
                },
                transformOrigin: {
                  vertical: "top",
                  horizontal: "left",
                },
                getContentAnchorEl: null,
              }}
            >
              {configObj["cropYears"].map((year) => (
                <MenuItem value={year}>{year}</MenuItem>
              ))}

              {/* <MenuItem value={2023}>2023</MenuItem> */}
            </Select>
          </FormControl>
        )}
        {indexName === "crop" && (
          <FormControl>
            <InputLabel
              shrink
              id="demo-simple-select-placeholder-label-label"
              className={classes.selectLabel}
            >
              Season
            </InputLabel>
            <Select
              variant="outlined"
              labelId="demo-simple-select-placeholder-label-label"
              id="demo-simple-select-placeholder-label"
              classes={{
                select: classes.select,
                icon: classes.selectIcon,
                outlined: classes.outlined,
              }}
              value={season}
              defaultValue={"world"}
              onChange={(event) => {
                dispatch({
                  type: setSeason,
                  payload: { season: event.target.value },
                });
              }}
              MenuProps={{
                anchorOrigin: {
                  vertical: "bottom",
                  horizontal: "left",
                },
                transformOrigin: {
                  vertical: "top",
                  horizontal: "left",
                },
                getContentAnchorEl: null,
              }}
            >
              <MenuItem value={"summer"}>Summer</MenuItem>
              <MenuItem value={"winter"}>Winter</MenuItem>

              {/* <MenuItem value={2023}>2023</MenuItem> */}
            </Select>
          </FormControl>
        )}

        {indexName !== "fields" &&
        indexName !== "Deforestation" &&
        indexName !== "crop" &&
        indexName !== "timeseries" ? (
          <FormControl>
            <InputLabel
              shrink
              id="demo-simple-select-placeholder-label-label"
              className={classes.selectLabel}
            >
              Field Sustainability Index
            </InputLabel>
            <Select
              variant="outlined"
              labelId="demo-simple-select-placeholder-label-label"
              id="demo-simple-select-placeholder-label"
              classes={{
                select: classes.select,
                icon: classes.selectIcon,
                outlined: classes.outlined,
              }}
              value={fbiLevel}
              defaultValue={"world"}
              onChange={(event) => {
                setLevel(event.target.value);
                dispatch({
                  type: setFBILevel,
                  payload: { fbiLevel: event.target.value },
                });
              }}
              MenuProps={{
                anchorOrigin: {
                  vertical: "bottom",
                  horizontal: "left",
                },
                transformOrigin: {
                  vertical: "top",
                  horizontal: "left",
                },
                getContentAnchorEl: null,
              }}
            >
              <MenuItem value={"local"}>Local FBI</MenuItem>
              <MenuItem value={"world"}>World FBI</MenuItem>
            </Select>
          </FormControl>
        ) : (
          ""
        )}
      </div>

      <Map onFieldClick={handleFieldClick} />
      <Dialog
        open={isYearSelectionModalOpen}
        onClose={() => setIsYearSelectionModalOpen(false)}
        className={classes.yearSelectionModal}
      >
        <DialogTitle>Select Year for Time Series Data</DialogTitle>
        <DialogContent>
          <div className={classes.yearGrid}>
            {[2018, 2019, 2020, 2021, 2022, 2023, 2024].map((year) => (
              <Button
                key={year}
                onClick={() => handleYearSelection(year)}
                className={`${classes.yearButton} ${
                  timeseriesYear === year ? classes.yearButtonSelected : ""
                }`}
              >
                {year}
              </Button>
            ))}
          </div>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setIsYearSelectionModalOpen(false)}
            color="primary"
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      <div className={classes.topRightContainer}>
        {!mobileView && <FileUpload label="" />}
      </div>

      <>
        {isLoading && (
          <div className={classes.circularProgressContainer}>
            <CircularProgress className={classes.circularProgress} />
            <Typography className={classes.loadingText}>
              Loading timeseries data
            </Typography>
          </div>
        )}
        <Dialog
          open={isTimeSeriesModalOpen}
          onClose={handleCloseTimeSeriesModal}
          fullScreen
        >
          <DialogTitle>
            Time Series Data for field id {selectedFieldData} and year{" "}
            {timeseriesYear}:
          </DialogTitle>
          <DialogContent className={classes.fieldResponseContainer}>
            <TimeSeriesChart data={fieldResponseJson} />
          </DialogContent>
          <DialogActions style={{ marginTop: "-58px" }}>
            <Button onClick={handleCloseTimeSeriesModal} color="primary">
              Close
            </Button>
          </DialogActions>
          <div className={classes.exportButtonContainer}>
            <Button
              className={classes.exportButton}
              onClick={downloadGeoJSON}
              disabled={!fieldResponseJson}
            >
              <DocumentationIcon classes={{ root: classes.infoIcon }} />
              <Typography variant="body2" fontWeight={700}>
                Save as Geojson
              </Typography>
            </Button>
          </div>
        </Dialog>
      </>

      <div className={classes.infoCardContainer}>
        <div>{feature ? <FieldStats /> : ""}</div>
      </div>
    </div>
  );
};
// Need Legend component.
// Need the selector component.
// Change the selector based on the zoom.
// For anything less than 13 local, 10-13 state, 6 country, 3 world.
// On hover details - use the component if it exists in other demos.

export default MapPage;
